import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FaFilePdf, FaUpload, FaSyncAlt, FaEye, FaTrashAlt, FaArrowLeft, FaExclamationTriangle,
} from "react-icons/fa";
import Modal from "../Modal";

const API = "/api/manuales";

const formatoTamano = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatoFecha = (fecha) =>
  fecha
    ? new Date(fecha).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

const nombreArchivo = (ruta) => (ruta ? ruta.split("/").pop() : "—");

export default function ManualesAdmin() {
  const [filas, setFilas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subiendoClave, setSubiendoClave] = useState(null);
  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });

  const fileInputRef = useRef(null);
  const claveObjetivo = useRef(null);
  const [filaAEliminar, setFilaAEliminar] = useState(null);

  const mostrarToast = (mensaje, tipo = "success") => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/matriz`, { credentials: "include" });
      if (!res.ok) throw new Error("Error obteniendo manuales");
      setFilas(await res.json());
    } catch (err) {
      console.error(err);
      mostrarToast("Error cargando manuales", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const triggerUpload = (clave) => {
    claveObjetivo.current = clave;
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    const clave = claveObjetivo.current;
    e.target.value = "";
    if (!file || !clave) return;

    const data = new FormData();
    data.append("archivo", file);

    setSubiendoClave(clave);
    try {
      const res = await fetch(`${API}/modulo/${clave}`, {
        method: "PUT",
        credentials: "include",
        body: data,
      });

      if (!res.ok) {
        const err = await res.json();
        mostrarToast(err.mensaje || "Error subiendo manual", "error");
        return;
      }

      mostrarToast("Manual guardado correctamente");
      cargar();
    } catch (err) {
      console.error(err);
      mostrarToast("Error inesperado", "error");
    } finally {
      setSubiendoClave(null);
    }
  };

  const confirmarEliminar = async () => {
    if (!filaAEliminar) return;

    try {
      const res = await fetch(`${API}/modulo/${filaAEliminar.clave}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        const err = await res.json();
        mostrarToast(err.mensaje || "Error quitando manual", "error");
        return;
      }

      mostrarToast("Manual desactivado");
      cargar();
    } catch (err) {
      console.error(err);
      mostrarToast("Error inesperado", "error");
    } finally {
      setFilaAEliminar(null);
    }
  };

  return (
    <div className="p-6 bg-slate-50">

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        onChange={onFileSelected}
        className="hidden"
      />

      <div className="mb-6">
        <Link to="/dashboard/administracion" className="text-sm text-slate-500 hover:text-indigo-600 flex items-center gap-1 mb-1">
          <FaArrowLeft className="text-xs" /> Administración
        </Link>
        <h1 className="text-2xl font-bold text-slate-800">Manuales</h1>
        <p className="text-sm text-slate-500">
          Carga el manual en PDF de cada módulo. Solo lo verán los usuarios con acceso a ese módulo.
        </p>
      </div>

      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Módulo</th>
                <th className="px-4 py-3 text-left font-semibold">Estado</th>
                <th className="px-4 py-3 text-left font-semibold">Archivo</th>
                <th className="px-4 py-3 text-left font-semibold">Tamaño</th>
                <th className="px-4 py-3 text-left font-semibold">Subido</th>
                <th className="px-4 py-3 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-10 text-slate-400">Cargando módulos...</td>
                </tr>
              ) : (
                filas.map((fila) => {
                  const tieneManual = !!fila.archivo;
                  const subiendo = subiendoClave === fila.clave;

                  return (
                    <tr key={fila.clave} className="border-t border-slate-100 hover:bg-slate-50 transition">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{fila.nombre}</p>
                        <p className="text-xs text-slate-400 font-mono">{fila.path}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                          tieneManual ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
                        }`}>
                          {tieneManual ? "Visible" : "Sin manual"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {tieneManual ? (
                          <span className="flex items-center gap-2 truncate max-w-[220px]">
                            <FaFilePdf className="text-rose-500 shrink-0" />
                            <span className="truncate">{nombreArchivo(fila.archivo)}</span>
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500">{formatoTamano(fila.tamano_bytes)}</td>
                      <td className="px-4 py-3 text-slate-500">{formatoFecha(fila.subido)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => triggerUpload(fila.clave)}
                            disabled={subiendo}
                            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition disabled:opacity-50"
                          >
                            {tieneManual ? <FaSyncAlt className="text-[10px]" /> : <FaUpload className="text-[10px]" />}
                            {subiendo ? "Subiendo..." : tieneManual ? "Reemplazar" : "Subir"}
                          </button>

                          {tieneManual && (
                            <>
                              <a
                                href={`${fila.archivo}`}
                                target="_blank"
                                rel="noreferrer"
                                title="Ver"
                                className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition"
                              >
                                <FaEye className="text-xs" />
                              </a>
                              <button
                                onClick={() => setFilaAEliminar(fila)}
                                title="Quitar"
                                className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition"
                              >
                                <FaTrashAlt className="text-xs" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL CONFIRMAR ELIMINAR */}
      <Modal open={!!filaAEliminar} onClose={() => setFilaAEliminar(null)} maxWidth="max-w-sm">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center mx-auto mb-4">
            <FaExclamationTriangle />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Quitar manual</h3>
          <p className="text-sm text-slate-500 mb-6">
            ¿Quitar el manual de <b>{filaAEliminar?.nombre}</b>? El archivo no se borra, solo deja de estar visible para los usuarios.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setFilaAEliminar(null)}
              className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={confirmarEliminar}
              className="w-full px-4 py-2 text-sm rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-medium transition"
            >
              Quitar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
